type ApiApp = 'core' | 'ai' | 'services' | 'marketplace' | 'background' | 'studio';

const PROD_HOSTS: Record<ApiApp, string> = {
  core: 'https://api-core.theoneaiplatform.com',
  ai: 'https://api-ai.theoneaiplatform.com',
  services: 'https://api-services.theoneaiplatform.com',
  marketplace: 'https://api-marketplace.theoneaiplatform.com',
  background: 'https://api-background.theoneaiplatform.com',
  studio: 'https://api-studio.theoneaiplatform.com',
};

const PREFIX_ROUTES: [string, ApiApp][] = [
  ['auth', 'core'],
  ['users', 'core'],
  ['tenants', 'core'],
  ['api-keys', 'core'],
  ['sso-bridge', 'core'],
  ['platform', 'core'],
  ['data-bridge', 'core'],
  ['ai/', 'ai'],
  ['governance', 'ai'],
  ['safety', 'ai'],
  ['models', 'ai'],
  ['custom-gpts', 'ai'],
  ['agents', 'ai'],
  ['assessments', 'ai'],
  ['content', 'ai'],
  ['workdna', 'ai'],
  ['strategy', 'ai'],
  ['enrichment', 'ai'],
  ['workflows', 'ai'],
  ['integration', 'ai'],
  ['chat/', 'ai'],
  ['memory', 'ai'],
  ['security', 'services'],
  ['voice', 'services'],
  ['phone', 'services'],
  ['web/', 'services'],
  ['email', 'services'],
  ['storage', 'services'],
  ['marketplace', 'marketplace'],
  ['academy', 'marketplace'],
  ['partner-hub', 'marketplace'],
  ['community', 'marketplace'],
  ['metering', 'background'],
  ['billing', 'background'],
  ['analytics', 'background'],
  ['studio/', 'studio'],
];

function resolveApp(path: string): ApiApp {
  for (const [prefix, app] of PREFIX_ROUTES) {
    if (path.startsWith(prefix)) return app;
  }
  return 'core';
}

function getBaseUrl(app: ApiApp): string {
  // In dev, proxy everything through Vite
  const devOverride = import.meta.env.VITE_API_URL;
  if (devOverride) return devOverride;

  if (import.meta.env.DEV) return '';

  return PROD_HOSTS[app];
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const app = resolveApp(path);
  const baseUrl = getBaseUrl(app);
  const url = new URL(`/api/${path}`, baseUrl || window.location.origin);

  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  const res = await fetch(url.toString(), {
    method: options.method ?? (options.body ? 'POST' : 'GET'),
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ error: 'Unknown error', code: 'UNKNOWN' }));
    throw new ApiError(
      res.status,
      errorBody.code ?? 'UNKNOWN',
      errorBody.error ?? `HTTP ${res.status}`,
      errorBody.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Studio API namespaces ──────────────────────────────────────────────────

export const studioBilling = {
  subscribe: () => api('studio/billing/subscribe', { method: 'POST' }),
  portal: () => api<{ url: string }>('studio/billing/portal', { method: 'POST' }),
  status: () => api<{
    subscribed: boolean; status: string; app_count: number; client_count: number;
    included_credits_usd: number; credits_used_usd: number;
    billing_period_start?: string | null; billing_period_end?: string | null;
    studio_platform_fee_usd: number; studio_app_fee_usd: number; studio_client_fee_usd: number;
    books_connected?: boolean;
  }>('studio/billing/status'),
};

export const studioStarters = {
  list: (params?: { category?: string; limit?: number; offset?: number }) =>
    api<{ starters: unknown[] }>('studio/starters', { query: params as Record<string, string> }),
  get: (id: string) => api<unknown>(`studio/starters/${id}`),
};

export const studioIdeas = {
  list: (params?: { vertical?: string; difficulty?: string }) =>
    api<{ ideas: unknown[] }>('studio/ideas', { query: params as Record<string, string> }),
  get: (id: string) => api<unknown>(`studio/ideas/${id}`),
};

export const studioOnboarding = {
  status: () => api<unknown>('studio/onboarding/status'),
  complete: (interview_answers: Record<string, string>) =>
    api<unknown>('studio/onboarding/complete', { method: 'POST', body: { interview_answers } }),
};

export const studioPsa = {
  status: () => api<unknown>('studio/psa/status'),
  connect: (body: { psa_type?: string; api_key: string; api_url?: string }) =>
    api<unknown>('studio/psa/connect', { method: 'POST', body }),
  disconnect: () => api<unknown>('studio/psa/disconnect', { method: 'DELETE' }),
};

export const studioRecommendations = {
  get: () => api<{ recommendations: unknown[] }>('studio/recommendations'),
};

export const studioProjects = {
  list: () => api<{ projects: unknown[] }>('studio/projects'),
  create: (body: { name: string; description?: string; starter_id?: string }) =>
    api<{ id: string }>('studio/projects', { method: 'POST', body }),
  get: (id: string) => api<unknown>(`studio/projects/${id}`),
  update: (id: string, body: Record<string, unknown>) =>
    api<unknown>(`studio/projects/${id}`, { method: 'PUT', body }),
  delete: (id: string) => api<unknown>(`studio/projects/${id}`, { method: 'DELETE' }),
  deploy: (id: string) => api<unknown>(`studio/projects/${id}/deploy`, { method: 'POST' }),
  undeploy: (id: string) => api<unknown>(`studio/projects/${id}/undeploy`, { method: 'POST' }),
};

export const studioBuilder = {
  updateBlocks: (projectId: string, blocks: unknown[]) =>
    api<unknown>(`studio/builder/${projectId}/blocks`, { method: 'PUT', body: { blocks } }),
  aiAssist: (
    projectId: string,
    body: { mode: 'explain' | 'customize' | 'build' | 'idea'; message: string; context?: Record<string, unknown> },
  ) => api<unknown>(`studio/builder/${projectId}/ai`, { method: 'POST', body }),
};

export const studioClients = {
  list: () => api<{ clients: unknown[] }>('studio/clients'),
  invite: (body: { client_name: string; client_email?: string; psa_company_id?: string; project_ids?: string[] }) =>
    api<unknown>('studio/clients/invite', { method: 'POST', body }),
  get: (id: string) => api<unknown>(`studio/clients/${id}`),
};

export const studioSecurity = {
  posture: (projectId: string) => api<unknown>(`studio/security/posture/${projectId}`),
  checklist: (projectId: string) => api<unknown>(`studio/security/checklist/${projectId}`),
};

export const studioDeployments = {
  status: (deploymentId: string) => api<unknown>(`studio/deployments/${deploymentId}/status`),
};

export const studioEmailSettings = {
  get: () => api<unknown>('studio/settings/email'),
  update: (body: { from_name: string; reply_to?: string | null }) =>
    api<unknown>('studio/settings/email', { method: 'PUT', body }),
  getDomain: () => api<unknown>('studio/settings/email/domain'),
  initDomain: (domain: string) =>
    api<unknown>('studio/settings/email/domain', { method: 'POST', body: { domain } }),
  pollDomain: () => api<unknown>('studio/settings/email/domain'),
  removeDomain: () => api<unknown>('studio/settings/email/domain', { method: 'DELETE' }),
};

// ── AI Chat API ───────────────────────────────────────────────────────────

export interface AIConversation {
  id: string;
  type: string;
  tenant_id: string;
  user_id: string;
  title: string;
  model_id: string;
  system_prompt?: string;
  pinned: boolean;
  archived: boolean;
  folder_id?: string;
  shared_with?: string[];
  message_count: number;
  total_tokens: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model_id?: string;
  tokens_input?: number;
  tokens_output?: number;
  cost_usd?: number;
  latency_ms?: number;
  created_at: string;
}

export interface MemoryEntry {
  id: string;
  content: string;
  category: 'preference' | 'fact' | 'instruction' | 'context';
  source: 'explicit' | 'inferred';
  confidence: number;
  created_at: string;
}

export interface UserPreferences {
  response_style: 'concise' | 'detailed' | 'technical' | 'balanced';
  timezone: string;
  role: string;
  specialties: string[];
  clients_managed: string[];
}

export interface AIFolder {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
}

export const aiChat = {
  conversations: {
    list: (params?: { archived?: boolean; pinned?: boolean; folder_id?: string; limit?: number; offset?: number }) =>
      api<{ conversations: AIConversation[] }>('chat/conversations', { query: params as Record<string, string> }),
    create: (body: { title?: string; model_id?: string; system_prompt?: string; folder_id?: string }) =>
      api<AIConversation>('chat/conversations', { method: 'POST', body }),
    get: (id: string) => api<{ conversation: AIConversation }>(`chat/conversations/${id}`),
    update: (id: string, body: Partial<Pick<AIConversation, 'title' | 'pinned' | 'archived' | 'model_id' | 'system_prompt' | 'folder_id'>>) =>
      api<AIConversation>(`chat/conversations/${id}`, { method: 'PUT', body }),
    delete: (id: string) => api<{ success: boolean }>(`chat/conversations/${id}`, { method: 'DELETE' }),
    share: (id: string, userIds: string[]) =>
      api<{ shared_with: string[] }>(`chat/conversations/${id}/share`, { method: 'POST', body: { user_ids: userIds } }),
    branch: (id: string, messageId: string) =>
      api<AIConversation>(`chat/conversations/${id}/branch`, { method: 'POST', body: { message_id: messageId } }),
    search: (q: string) =>
      api<{ conversations: AIConversation[]; messages: { conversation_id: string; content: string; role: string; created_at: string }[] }>('chat/conversations/search', { query: { q } }),
  },
  messages: {
    list: (conversationId: string) =>
      api<{ messages: AIMessage[] }>(`chat/conversations/${conversationId}/messages`),
    send: (conversationId: string, body: { content: string; model_id?: string }) =>
      api<{ message: AIMessage; conversation: { title: string; message_count: number } }>(`chat/conversations/${conversationId}/messages`, { method: 'POST', body }),
    delete: (conversationId: string, messageId: string) =>
      api<{ success: boolean }>(`chat/conversations/${conversationId}/messages/${messageId}`, { method: 'DELETE' }),
    regenerate: (conversationId: string) =>
      api<{ message: AIMessage }>(`chat/conversations/${conversationId}/regenerate`, { method: 'POST' }),
  },
  folders: {
    list: () => api<{ folders: AIFolder[] }>('chat/folders'),
    create: (name: string) => api<AIFolder>('chat/folders', { method: 'POST', body: { name } }),
    update: (id: string, name: string) => api<AIFolder>(`chat/folders/${id}`, { method: 'PUT', body: { name } }),
    delete: (id: string) => api<{ success: boolean }>(`chat/folders/${id}`, { method: 'DELETE' }),
  },
  memory: {
    get: () => api<{ memories: MemoryEntry[]; preferences: UserPreferences }>('memory'),
    add: (content: string, category?: string) => api<MemoryEntry>('memory', { method: 'POST', body: { content, category } }),
    update: (id: string, body: { content?: string; category?: string }) => api<MemoryEntry>(`memory/${id}`, { method: 'PUT', body }),
    delete: (id: string) => api<{ success: boolean }>(`memory/${id}`, { method: 'DELETE' }),
    clear: () => api<{ success: boolean }>('memory/clear', { method: 'POST' }),
    getPreferences: () => api<UserPreferences>('memory/preferences'),
    updatePreferences: (prefs: Partial<UserPreferences>) => api<UserPreferences>('memory/preferences', { method: 'PUT', body: prefs }),
  },
};

export const studioAIDivision = {
  setup: (body: {
    division_name: string;
    tagline?: string;
    focus_areas: string[];
    target_market: string;
  }) => api<{ brand_id: string; website_id: string; brand_url: string }>('studio/ai-division', { method: 'POST', body }),
  status: () => api<{
    setup: boolean;
    brand_id?: string;
    website_id?: string;
    brand_url?: string;
    division_name?: string;
    setup_at?: string;
  }>('studio/ai-division/status'),
};

export const studioBooks = {
  setupIntent: () => api<{ clientSecret: string; publishableKey: string }>('studio/books/setup-intent', { method: 'POST' }),
  subscribe: (body: { payment_method_id: string; plan_id?: string }) =>
    api<unknown>('studio/books/subscribe', { method: 'POST', body }),
  invoices: () => api<{ invoices: unknown[] }>('studio/books/invoices'),
  connect: (books_api_key: string) =>
    api<unknown>('studio/books/connect', { method: 'POST', body: { books_api_key } }),
  disconnect: () => api<unknown>('studio/books/disconnect', { method: 'DELETE' }),
};
